/**
 * Sessão do usuário NOC (S3.2 — login/sessão multi-tenant).
 *
 * Persiste o token de login em localStorage. Modelo ADITIVO: convive com o
 * ADMIN_TOKEN de bancada (config.js). O api.js prefere o token de sessão
 * (Authorization: Bearer) quando presente; senão usa o X-Admin-Token (fallback
 * de bancada). Logout limpa apenas a sessão.
 *
 * NÃO grava senha — apenas o token de sessão (Fernet, expira no backend) e
 * dados não-sensíveis (email, role, tenant) para exibição na topbar.
 */

const STORAGE_KEY = "ipwd-noc-session-v1";

export function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    return s && s.token ? s : null;
  } catch {
    return null;
  }
}

export function saveSession(session) {
  const safe = {
    token: String(session?.token || ""),
    email: String(session?.email || ""),
    role: String(session?.role || ""),
    tenantId: session?.tenantId ? String(session.tenantId) : "",
    tenantName: String(session?.tenantName || ""),
    fullName: String(session?.fullName || ""),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
  return safe;
}

export function clearSession() {
  localStorage.removeItem(STORAGE_KEY);
}

export function hasSession() {
  return !!loadSession();
}

// ─────────────────────────────────────────────────────────────────────────
// SAAS-AUTH-RECOVERY-MODE-1 — recovery EXPLÍCITO via X-Admin-Token.
//
// O fluxo normal é o login Bearer. Um ADMIN_TOKEN configurado NÃO autentica
// sozinho: só libera acesso quando o operador ativa explicitamente o modo de
// recuperação (botão "Acesso de recuperação" no Login). A flag abaixo marca esse
// estado; logout a limpa; um 401 de sessão Bearer NÃO a apaga (recovery
// preservado p/ evitar lockout).
// ─────────────────────────────────────────────────────────────────────────
const RECOVERY_KEY = "ipwd-noc-recovery-v1";

export function enableRecovery() {
  try {
    localStorage.setItem(RECOVERY_KEY, "1");
  } catch {
    /* noop */
  }
}

export function isRecoveryActive() {
  try {
    return localStorage.getItem(RECOVERY_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearRecovery() {
  try {
    localStorage.removeItem(RECOVERY_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Decisão PURA de "está autenticado para entrar no NOC".
 *
 * Bearer de sessão entra sempre. ADMIN_TOKEN só entra quando o modo de
 * recuperação está EXPLICITAMENTE ativo — nunca de forma automática só por
 * existir no localStorage. Mantém o recovery como escape hatch sem lockout.
 */
export function authedFrom(session, adminToken, recoveryActive) {
  if (session) return true;
  return !!recoveryActive && !!adminToken;
}
